// src/api/cashReports.js
import { supabase } from './supabaseClient'; // Adjust path if needed

/**
 * Fetches the currently authenticated user.
 * @returns {Promise<Object|null>} The user object or null if not authenticated
 * @throws {Error} If the request fails
 */
export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw new Error(`Failed to get user: ${error.message}`);
    return user;
  } catch (error) {
    console.error('getCurrentUser error:', error.message);
    throw error;
  }
}

/**
 * Loads a cash report for a specific date.
 * @param {string} dateKey - The date in YYYY-MM-DD format
 * @returns {Promise<Array|null>} The report data or null if not found
 * @throws {Error} If the request fails
 */
export async function loadReport(dateKey) {
  try {
    const { data, error } = await supabase
      .from('cash_reports')
      .select('data, opening_balance') // Select both fields
      .eq('report_date', dateKey)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      throw new Error(`Failed to load report: ${error.message}`);
    }

    // Return both data and opening_balance if a row exists, otherwise null
    return data ? { data: data.data, opening_balance: data.opening_balance } : null;
  } catch (error) {
    console.error('loadReport error:', error.message);
    throw error;
  }
}

/**
 * Updates the opening balance for a specific date’s existing report.
 * @param {string} dateKey - The date in YYYY-MM-DD format
 * @param {number} openingBalance - The opening balance to save
 * @param {string} userId - The ID of the user (optional, if part of schema)
 * @returns {Promise<void>}
 * @throws {Error} If the request fails or no row exists
 */
export async function saveOpening(dateKey, openingBalance, userId) {
  try {
    const { error } = await supabase
      .from('cash_reports')
      .update({ opening_balance: openingBalance })
      .eq('report_date', dateKey)
      // Uncomment the next line if your table has a user_id column and you want to filter by it
      // .eq('user_id', userId);
    
    if (error) throw new Error(`Failed to save opening balance: ${error.message}`);
  } catch (error) {
    console.error('saveOpening error:', error.message);
    throw error;
  }
}

/**
 * Saves or updates a cash report for a specific date.
 * @param {string} dateKey - The date in YYYY-MM-DD format
 * @param {Array} reportData - The report data to save
 * @returns {Promise<void>}
 * @throws {Error} If the request fails
 */
export async function saveReport(dateKey, reportData) {
  try {
    const { error } = await supabase
      .from('cash_reports')
      .upsert(
        { report_date: dateKey, data: reportData },
        { onConflict: ['report_date'] } // Now works after adding UNIQUE constraint
      );

    if (error) throw new Error(`Failed to save report: ${error.message}`);
  } catch (error) {
    console.error('saveReport error:', error.message);
    throw error;
  }
}
